/**
 * Uniform Rescale
 *
 * Moves a set of canvas objects from one print area to another with ONE
 * scale factor for both axes. This is the pure-math core behind every
 * "same design, different product" transform.
 *
 * Invariant: automatic adaptation never changes an object's scaleX/scaleY
 * ratio. Only the user may distort a design, and a user distortion survives
 * the transform because both axes are multiplied by the same factor.
 *
 * The placement rule is `carryOverPlacement` below; WHICH rule applies is
 * decided by `carryOverRule` (identity, RUNDUM, percent box, class change,
 * KLEIN, relative), and every fit goes through the one `fitInside`.
 */

import {
  computeRelativeDesignStateFromJSON,
  SYSTEM_LAYER_NAME_SET,
  type CanvasObjectJSON,
} from './computeDesignState.js';
import type {
  AdaptedPlacement,
  RelativeDesignState,
} from './placementTypes.js';
import { carryOverRule, type CarryOverRuleOptions } from './carryOverRule.js';
import { fitInside, overflows, relHeightOn } from './fit.js';
import { fitIntoBox, fitIntoRect } from './fitIntoBox.js';
import { maximisesOnTier, type PlacementArea } from '../placement/sizeTier.js';

// =============================================================================
// TYPES
// =============================================================================

/**
 * A uniform similarity transform between two print areas: scale about the
 * design's group center, then move that center to its target position.
 * All coordinates are print-area-relative pixels (no canvas offset).
 */
export interface UniformRescale {
  /** Factor applied to scaleX AND scaleY of every object */
  scale: number;
  /** Design group center on the source print area */
  sourceCenterX: number;
  sourceCenterY: number;
  /** Design group center on the target print area */
  targetCenterX: number;
  targetCenterY: number;
}

// =============================================================================
// PLACEMENT RULE
// =============================================================================

export { fitInside, overflows } from './fit.js';

/**
 * Whether a maximising branch applies between two areas: a KLEIN or RUNDUM
 * target and a source whose tier is known and different.
 *
 * @deprecated Ask `carryOverRule` and `isTargetDrivenRule` instead; this
 * only knows about tiers, not about percents or classes.
 */
export function maximisesOnTarget(
  source: PlacementArea,
  target: PlacementArea
): boolean {
  return maximisesOnTier(source.sizeTier, target.sizeTier);
}

/**
 * The seed width of the KLEIN rule: the whole motif, never wider in mm than
 * on the master. Without mm on both sides there is no cap and the seed is
 * the full width.
 */
function kleinSeedRelWidth(
  rel: RelativeDesignState,
  source: PlacementArea,
  target: PlacementArea
): number {
  const sourceMm = source.maxWidthMm ?? 0;
  const targetMm = target.maxWidthMm ?? 0;
  return sourceMm > 0 && targetMm > 0
    ? Math.min(1, (rel.relWidth * sourceMm) / targetMm)
    : 1;
}

/**
 * The RUNDUM rule: the whole motif as large as the FRONT HALF of the strip
 * takes it, centered on 25 % of the width. That is where a mug shows its
 * front (the strip's ends meet at the handle, its middle is opposite), the
 * convention Phoenix and the shirtee.com importer place by as well. Never
 * capped at the master width: the tier is admin-set and means "fill".
 */
function maximisedOnFrontHalf(
  rel: RelativeDesignState,
  target: PlacementArea
): RelativeDesignState {
  return fitIntoRect(rel, target, {
    left: 0,
    top: 0,
    width: target.width / 2,
    height: target.height,
  });
}

/**
 * The relative rule: a design that FIT its source area (within the shared
 * tolerance) is shrunk uniformly to at most 100 % of the target and clamped
 * inside; a design the user deliberately let OVERFLOW its source keeps that
 * overflow proportionally, the same crop the product cards show.
 */
function relativeRule(
  rel: RelativeDesignState,
  source: PlacementArea,
  target: PlacementArea
): RelativeDesignState {
  return overflows(rel, source) ? { ...rel } : fitInside(rel, target);
}

/**
 * Where a design goes when its print area changes. The decision is
 * `carryOverRule`; this applies it:
 *
 * - `master` / `custom`: the state describes this very area. A master state
 *   is read exactly as the relative rule reads it (identity for a design
 *   that fit its recorded source, clamped into the area for an overshoot
 *   within tolerance), so a row means the same before and after the
 *   carry-over settings. The overflow test runs against the recorded source,
 *   not the current area: a catalogue change that shrank the master's print
 *   area after the design was saved must not turn a fitting design into a
 *   "deliberate" overflow.
 * - `rundum`: the whole motif in the front half of the strip.
 * - `percent`, `object-to-textile`, `textile-to-object`: the whole motif,
 *   centered, fitted into the rule's box (`fitIntoBox`).
 * - `klein`: the whole motif, centered, maximised, capped at the master mm.
 * - `relative`: relative width and center kept, fitted or overflowing.
 *
 * Since 2026-09 this is the single placement rule for the live editor, the
 * single-product load, the product cards, the backend batch-apply, the
 * print files and the agent tools. `options.isMaster` is required: a reader
 * that does not know the master cannot resolve the row (compile error
 * instead of a silent divergence).
 */
export function carryOverPlacement(
  rel: RelativeDesignState,
  source: PlacementArea,
  target: PlacementArea,
  options: CarryOverRuleOptions
): RelativeDesignState {
  const decision = carryOverRule(source, target, options);
  switch (decision.rule) {
    case 'custom':
      return { ...rel };
    case 'master':
      return relativeRule(rel, source, target);
    case 'rundum':
      return maximisedOnFrontHalf(rel, target);
    case 'klein':
      return fitIntoRect(
        rel,
        target,
        { left: 0, top: 0, width: target.width, height: target.height },
        kleinSeedRelWidth(rel, source, target)
      );
    case 'percent':
    case 'object-to-textile':
    case 'textile-to-object':
      return fitIntoBox(rel, source, target, decision.box).placement;
    case 'relative':
      return relativeRule(rel, source, target);
  }
}

/**
 * Convert a center-based relative state to the edge-based, CSS-ready
 * placement WITHOUT clamping or contain-fit. Edges may lie outside 0-1 when
 * the design overflows the print area; the renderer clips to the area.
 */
export function placementFromRelative(
  rel: RelativeDesignState,
  target: PlacementArea
): AdaptedPlacement {
  const relHeight = relHeightOn(rel, target);
  return {
    relLeft: rel.relCenterX - rel.relWidth / 2,
    relTop: rel.relCenterY - relHeight / 2,
    relWidth: rel.relWidth,
    relHeight,
    designRatio: rel.designRatio,
    minSizeEnforced: false,
  };
}

// =============================================================================
// TRANSFORM
// =============================================================================

/**
 * Compute the uniform transform that carries a design from `source` to
 * `target`, landing on `targetRel` (the result of `carryOverPlacement`, or
 * an explicit placement such as a restored switch anchor).
 */
export function computeUniformRescale(
  rel: RelativeDesignState,
  source: PlacementArea,
  target: PlacementArea,
  targetRel: RelativeDesignState
): UniformRescale {
  const sourceWidthPx = rel.relWidth * source.width;
  const targetWidthPx = targetRel.relWidth * target.width;

  return {
    scale: sourceWidthPx > 0 ? targetWidthPx / sourceWidthPx : 1,
    sourceCenterX: rel.relCenterX * source.width,
    sourceCenterY: rel.relCenterY * source.height,
    targetCenterX: targetRel.relCenterX * target.width,
    targetCenterY: targetRel.relCenterY * target.height,
  };
}

/**
 * Where the rescaled design lands: an explicit placement (a restored switch
 * anchor, a resolved row), or the rule options for `carryOverPlacement`.
 */
export type RescaleTargetPlacement = RelativeDesignState | CarryOverRuleOptions;

function isExplicitPlacement(
  placement: RescaleTargetPlacement
): placement is RelativeDesignState {
  return 'relWidth' in placement;
}

/**
 * Rescale serialized canvas objects (print-area-relative coordinates) from
 * `source` to `target`. Returns new objects; the input is not mutated.
 * System layers pass through untouched.
 *
 * `placement` is either the explicit target state or the carry-over options
 * (then the design lands where `carryOverPlacement` puts it). There is no
 * shortcut for equal sizes: two areas of the same millimetres can still
 * differ in product class or percent, and the rule decides that.
 */
export function rescaleCanvasObjectsJSON(
  objects: CanvasObjectJSON[],
  source: PlacementArea,
  target: PlacementArea,
  placement: RescaleTargetPlacement
): CanvasObjectJSON[] {
  const explicit = isExplicitPlacement(placement);

  const rel = computeRelativeDesignStateFromJSON(
    { objects },
    {
      widthPx: source.width,
      heightPx: source.height,
      offsetX: 0,
      offsetY: 0,
    }
  );
  if (!rel) return objects;

  const targetRel = explicit
    ? placement
    : carryOverPlacement(rel, source, target, placement);
  const t = computeUniformRescale(rel, source, target, targetRel);

  return objects.map((obj) => {
    if (SYSTEM_LAYER_NAME_SET.has(obj.name ?? '')) return obj;

    const out: CanvasObjectJSON = { ...obj };
    if (typeof obj.left === 'number') {
      out.left = t.targetCenterX + (obj.left - t.sourceCenterX) * t.scale;
    }
    if (typeof obj.top === 'number') {
      out.top = t.targetCenterY + (obj.top - t.sourceCenterY) * t.scale;
    }
    out.scaleX = (obj.scaleX ?? 1) * t.scale;
    out.scaleY = (obj.scaleY ?? 1) * t.scale;
    return out;
  });
}

// =============================================================================
// GEOMETRY SIGNATURE
// =============================================================================

/** Properties that define where and how large an object renders. */
const GEOMETRY_KEYS = [
  'type',
  'left',
  'top',
  'width',
  'height',
  'scaleX',
  'scaleY',
  'angle',
  'originX',
  'originY',
  'skewX',
  'skewY',
  'flipX',
  'flipY',
  'strokeWidth',
] as const;

interface GeometryOnly {
  [key: string]: unknown;
  objects?: GeometryOnly[];
}

function pickGeometry(obj: CanvasObjectJSON): GeometryOnly {
  // Serialized Fabric objects carry more geometry (skew, flip) than the
  // minimal CanvasObjectJSON type declares; read them through a plain record.
  const source = obj as Record<string, unknown>;
  const out: GeometryOnly = {};
  for (const key of GEOMETRY_KEYS) {
    const value = source[key];
    if (value !== undefined) {
      out[key] = value;
    }
  }
  if (obj.objects?.length) {
    out.objects = obj.objects.map(pickGeometry);
  }
  return out;
}

/**
 * Signature of the geometry of a set of objects. Equal signatures mean the
 * design was not moved, resized, rotated or restructured -- non-geometric
 * edits (fill, text content, filters) do not change it.
 *
 * Used to tell "the user edited the design" apart from "the last product
 * switch produced this state", so a switch can be recomputed from the
 * user's last edit instead of accumulating rounding on every hop.
 */
export function computeGeometrySignature(objects: CanvasObjectJSON[]): string {
  return JSON.stringify(objects.map(pickGeometry));
}
